/**
 * Reporting periods in the studio's timezone. The owner UI and the owner assistant
 * both resolve presets through this one function, so "this week" means the same
 * dates everywhere; SQL then turns the local dates into exact instants.
 */
export type PeriodPreset = 'today' | 'yesterday' | 'tomorrow' | 'week' | 'last_week' | 'month' | 'last_month' | 'next_7_days';

export interface Period {
  preset: PeriodPreset | 'custom';
  from: string; // YYYY-MM-DD, tenant-local, inclusive
  to: string; // inclusive
  timezone: string;
  label: string;
}

export const PERIOD_PRESETS: PeriodPreset[] = ['today', 'yesterday', 'tomorrow', 'week', 'last_week', 'month', 'last_month', 'next_7_days'];

const LABEL: Record<PeriodPreset, string> = {
  today: 'Сегодня',
  yesterday: 'Вчера',
  tomorrow: 'Завтра',
  week: 'Эта неделя',
  last_week: 'Прошлая неделя',
  month: 'Этот месяц',
  last_month: 'Прошлый месяц',
  next_7_days: 'Ближайшие 7 дней',
};

/** Tenant-local calendar date for an instant. */
export function localDate(instant: Date, timezone: string): string {
  // en-CA yields YYYY-MM-DD
  return new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(instant);
}

function parse(d: string): Date {
  return new Date(`${d}T00:00:00Z`);
}
function fmt(d: Date): string {
  return d.toISOString().slice(0, 10);
}
export function addDays(d: string, n: number): string {
  const x = parse(d);
  x.setUTCDate(x.getUTCDate() + n);
  return fmt(x);
}
/** ISO weekday 1..7 of a calendar date. */
export function isoWeekday(d: string): number {
  const w = parse(d).getUTCDay();
  return w === 0 ? 7 : w;
}

export function resolvePeriod(preset: PeriodPreset, timezone: string, now = new Date()): Period {
  const today = localDate(now, timezone);
  const monday = addDays(today, 1 - isoWeekday(today));
  const firstOfMonth = today.slice(0, 8) + '01';
  const range = ((): [string, string] => {
    switch (preset) {
      case 'today':
        return [today, today];
      case 'yesterday':
        return [addDays(today, -1), addDays(today, -1)];
      case 'tomorrow':
        return [addDays(today, 1), addDays(today, 1)];
      case 'week':
        return [monday, addDays(monday, 6)];
      case 'last_week':
        return [addDays(monday, -7), addDays(monday, -1)];
      case 'next_7_days':
        return [today, addDays(today, 6)];
      case 'month': {
        const next = parse(firstOfMonth);
        next.setUTCMonth(next.getUTCMonth() + 1);
        return [firstOfMonth, addDays(fmt(next), -1)];
      }
      case 'last_month': {
        const prev = parse(firstOfMonth);
        prev.setUTCMonth(prev.getUTCMonth() - 1);
        return [fmt(prev), addDays(firstOfMonth, -1)];
      }
    }
  })();
  return { preset, from: range[0], to: range[1], timezone, label: LABEL[preset] };
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
export function customPeriod(from: string, to: string, timezone: string): Period {
  if (!DATE_RE.test(from) || !DATE_RE.test(to) || to < from) throw new Error('invalid_input');
  if ((parse(to).getTime() - parse(from).getTime()) / 864e5 > 366) throw new Error('invalid_input');
  return { preset: 'custom', from, to, timezone, label: `${from} — ${to}` };
}
